/**
 * Conversation Utility Functions
 *
 * Common operations and helpers for working with Chatwoot conversations.
 */

import { format } from "date-fns";
import { ChatwootConversation, ConversationStatus } from "@/services/frontdesk/api/chatwoot/types/conversations.types";

/**
 * Extracts only the new reply from an email message, stripping quoted content.
 *
 * Two-tier approach:
 * 1. HTML detection (most reliable): Gmail uses `class="gmail_quote"` which is
 *    NOT localized — same CSS class in every language. If HTML content is available,
 *    we strip everything inside `<div class="gmail_quote">`.
 * 2. Plain text fallback: Multi-language regex patterns for the "On ... wrote:" line
 *    covering English, Hebrew, Arabic, German, French, Spanish, Italian, Portuguese,
 *    Dutch, Polish, Russian, Swedish/Norwegian/Danish, Turkish, Chinese, Japanese, Korean.
 *
 * @param content - The plain text message content
 * @param htmlContent - Optional HTML content (from content_attributes.email.html_content.full)
 * @returns Only the new reply text, trimmed
 */
export function stripEmailQuotes(content: string, htmlContent?: string): string {
  if (!content) return '';

  // Tier 1: HTML-based detection (language-agnostic, most reliable)
  if (htmlContent) {
    const htmlReply = extractReplyFromHtml(htmlContent);
    if (htmlReply !== null) return htmlReply;
  }

  // Tier 2: Plain text multi-language pattern matching
  return stripPlainTextQuotes(content);
}

/**
 * Extracts the reply portion from Gmail HTML by removing the gmail_quote div.
 * Returns null if no Gmail quote structure is found (caller should fall back to plain text).
 */
function extractReplyFromHtml(html: string): string | null {
  // Gmail wraps all quoted content in <div class="gmail_quote">
  const quoteIndex = html.indexOf('class="gmail_quote"');
  if (quoteIndex === -1) return null;

  // Find the opening <div that contains the gmail_quote class
  const divStart = html.lastIndexOf('<div', quoteIndex);
  if (divStart === -1) return null;

  // Everything before the gmail_quote div is the reply
  const replyHtml = html.substring(0, divStart);

  // Strip HTML tags to get plain text
  return htmlToPlainText(replyHtml).trim() || null;
}

/**
 * Simple HTML to plain text conversion for extracting reply content.
 */
function htmlToPlainText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// Multi-language "wrote:" attribution line patterns
// Gmail localizes the "On [date], [name] wrote:" line based on sender's locale
const QUOTE_START_PATTERNS: RegExp[] = [
  // English: "On Mon, Mar 2, 2026 at 3:58 PM ... wrote:"
  /^On .+wrote[\s\u00a0]?:/m,
  // German: "Am 30. August 2013 09:41 schrieb Name:" or "Name <email> schrieb:"
  /^Am .+schrieb .+:/m,
  /^.+<.+>\s*schrieb:/m,
  // French: "Le 27 juin 2014 à 22:10, ... a écrit :"
  /^Le .+[ée]crit[\s\u00a0]?:/m,
  // Spanish: "El 27 de junio de 2014, ... escribió:"
  /^El .+escribi[oó][\s\u00a0]?:/m,
  // Italian: "Il giorno ... ha scritto:"
  /^Il .+scritto[\s\u00a0]?:/m,
  // Portuguese: "Em ... escreveu:"
  /^Em .+escreveu[\s\u00a0]?:/m,
  // Dutch: "Op ... schreef/geschreven ...:"
  /^Op .+(?:schreef|geschreven) .+:/m,
  // Polish: "W dniu ... napisał(a):" / "Dnia ... pisze:"
  /^(?:W dniu|Dnia) .+(?:napisa[lł](?:\(a\))?|pisze)[\s\u00a0]?:/m,
  // Russian: "... написал(а):"
  /^.+написал[аов]?[\s\u00a0]?:/m,
  // Swedish/Norwegian/Danish: "Den ... skrev ...:"
  /^Den .+skrev .+:/m,
  // Danish abbreviated: "fre. ... skrev ...:"
  /^[a-zæøå]{2,4}\.\s.+skrev\s.+:/m,
  // Turkish: "... tarihinde ... yazdı:"
  /^.+tarihinde .+yazd[ıi][\s\u00a0]?:/m,
  // Hebrew: "בתאריך ... כתב:"
  /^בתאריך .+כתב(?:\(ה\))?[\s\u00a0]?:/m,
  // Arabic: "في ... كتب ...:"
  /^في .+كتب.+:/m,
  // Chinese: "在 ... 写道："
  /^在.+写道[\s\u00a0]?[：:]/m,
  // Japanese: "20XX/XX/XX のメッセージ:"
  /^20\d{2}\/.+のメッセージ[\s\u00a0]?:/m,
  // Korean: "20XX. ... 작성:"
  /^20\d{2}\..+작성[\s\u00a0]?:/m,
  // Catalan: "El ... ha escrit:"
  /^El .+ha escrit[\s\u00a0]?:/m,
  // Czech: "... napsal:"
  /^.+napsal[\s\u00a0]?:/m,
  // Forwarded message (localized variants)
  /^-{5,}\s*Forwarded message\s*-{5,}$/m,
  /^-{5,}\s*Message transf[ée]r[ée]\s*-{5,}$/m,
  /^-{5,}\s*Weitergeleitete Nachricht\s*-{5,}$/m,
  /^-{5,}\s*Mensaje reenviado\s*-{5,}$/m,
  // Outlook: "-----Original Message-----"
  /^-{5,}\s*Original Message\s*-{5,}$/im,
  // Generic "From:" header (Outlook-style, with email in brackets)
  /^(?:From|De|Van|Da|Von|发件人|מאת)\s?:.+[\[<].+[\]>]/m,
];

/**
 * Strips quoted content from plain text email using multi-language patterns.
 */
function stripPlainTextQuotes(content: string): string {
  // Normalize \r\n (email line endings) to \n
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  let cutIndex = lines.length;

  // Find the first line that matches any quote attribution pattern
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Check all localized attribution patterns
    for (const pattern of QUOTE_START_PATTERNS) {
      if (pattern.test(line)) {
        cutIndex = i;
        break;
      }
    }
    if (cutIndex !== lines.length) break;
  }

  // Take everything before the quote start, removing trailing ">" quoted lines
  const resultLines: string[] = [];
  for (let i = 0; i < cutIndex; i++) {
    if (/^>/.test(lines[i].trim())) continue;
    resultLines.push(lines[i]);
  }

  return resultLines.join('\n').trim();
}

/**
 * Gets the display name for a conversation
 *
 * @param conversation - The conversation
 * @returns The sender's name or email, or "Unknown" if unavailable
 *
 * @example
 * ```typescript
 * const name = getConversationName(conversation); // "John Doe"
 * ```
 */
export function getConversationName(conversation: ChatwootConversation): string {
  return conversation.meta?.sender?.name || conversation.meta?.sender?.email || "Unknown";
}

/**
 * Gets the conversation channel/inbox name
 *
 * @param conversation - The conversation
 * @returns The channel name (e.g., "Email", "WhatsApp")
 *
 * @example
 * ```typescript
 * const channel = getConversationChannel(conversation); // "Email"
 * ```
 */
export function getConversationChannel(conversation: ChatwootConversation): string {
  const channel = conversation.meta?.channel;
  if (!channel) return "Unknown";

  // Clean up channel names (e.g., "Channel::Email" -> "Email")
  if (channel.includes("::")) {
    return channel.split("::")[1];
  }

  return channel;
}

/**
 * Checks if a conversation is unread
 *
 * @param conversation - The conversation
 * @returns True if the conversation has unread messages
 *
 * @example
 * ```typescript
 * if (isUnread(conversation)) {
 *   // Show unread indicator
 * }
 * ```
 */
export function isUnread(conversation: ChatwootConversation): boolean {
  return (conversation.unread_count ?? 0) > 0;
}

/**
 * Checks if a conversation is resolved
 *
 * @param conversation - The conversation
 * @returns True if the conversation is resolved
 *
 * @example
 * ```typescript
 * if (isResolved(conversation)) {
 *   // Show resolved badge
 * }
 * ```
 */
export function isResolved(conversation: ChatwootConversation): boolean {
  return conversation.status === ConversationStatus.RESOLVED;
}

/**
 * Checks if a conversation is assigned to someone
 *
 * @param conversation - The conversation
 * @returns True if the conversation has an assignee
 *
 * @example
 * ```typescript
 * if (isAssigned(conversation)) {
 *   // Show assignee avatar
 * }
 * ```
 */
export function isAssigned(conversation: ChatwootConversation): boolean {
  return conversation.meta?.assignee !== null && conversation.meta?.assignee !== undefined;
}

/**
 * Gets the last message preview text
 *
 * @param conversation - The conversation
 * @param maxLength - Maximum length of preview (default: 100)
 * @returns Truncated message content
 *
 * @example
 * ```typescript
 * const preview = getMessagePreview(conversation, 50); // "Hello, I have a question about..."
 * ```
 */
export function getMessagePreview(conversation: ChatwootConversation, maxLength: number = 100): string {
  const lastMessage = conversation.last_non_activity_message;
  if (!lastMessage?.content) return "";

  const content = lastMessage.content.trim();
  if (content.length <= maxLength) return content;

  return content.substring(0, maxLength) + "...";
}

/**
 * Formats a timestamp to relative time (e.g., "2 hours ago")
 *
 * @param timestamp - Unix timestamp in seconds
 * @returns Relative time string
 *
 * @example
 * ```typescript
 * const timeAgo = formatRelativeTime(conversation.last_activity_at); // "5 minutes ago"
 * ```
 */
export function formatRelativeTime(timestamp?: number): string {
  if (!timestamp) return "";

  const now = Date.now() / 1000;
  const diffSeconds = now - timestamp;

  if (diffSeconds < 60) return "just now";
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
  if (diffSeconds < 604800) return `${Math.floor(diffSeconds / 86400)}d ago`;

  // Format as day/month/year
  return format(new Date(timestamp * 1000), "d/M/yyyy");
}

/**
 * Gets a color for conversation status
 *
 * @param status - The conversation status
 * @returns A color string (e.g., "#10B981" for success)
 *
 * @example
 * ```typescript
 * const color = getStatusColor(conversation.status); // "#10B981"
 * ```
 */
export function getStatusColor(status?: string): string {
  switch (status) {
    case ConversationStatus.OPEN:
      return "#3B82F6"; // Blue
    case ConversationStatus.RESOLVED:
      return "#10B981"; // Green
    case ConversationStatus.PENDING:
      return "#F59E0B"; // Orange
    case ConversationStatus.SNOOZED:
      return "#6B7280"; // Gray
    default:
      return "#6B7280"; // Gray
  }
}

/**
 * Sorts conversations by last activity (most recent first)
 *
 * @param conversations - Array of conversations
 * @returns Sorted array (does not mutate original)
 *
 * @example
 * ```typescript
 * const sorted = sortByLastActivity(conversations);
 * ```
 */
export function sortByLastActivity(conversations: ChatwootConversation[]): ChatwootConversation[] {
  return [...conversations].sort((a, b) => {
    const aTime = a.last_activity_at ?? 0;
    const bTime = b.last_activity_at ?? 0;
    return bTime - aTime;
  });
}

/**
 * Filters conversations by search query
 *
 * Searches in: sender name, sender email, message content
 *
 * @param conversations - Array of conversations
 * @param query - Search query
 * @returns Filtered array
 *
 * @example
 * ```typescript
 * const results = searchConversations(conversations, "john");
 * ```
 */
export function searchConversations(conversations: ChatwootConversation[], query: string): ChatwootConversation[] {
  if (!query.trim()) return conversations;

  const lowerQuery = query.toLowerCase();

  return conversations.filter((conv) => {
    const senderName = conv.meta?.sender?.name?.toLowerCase() || "";
    const senderEmail = conv.meta?.sender?.email?.toLowerCase() || "";
    const messageContent = conv.last_non_activity_message?.content?.toLowerCase() || "";

    return (
      senderName.includes(lowerQuery) ||
      senderEmail.includes(lowerQuery) ||
      messageContent.includes(lowerQuery)
    );
  });
}

/**
 * Sorts snoozed conversations by urgency
 *
 * Overdue snoozed conversations appear first, sorted by how long they've been overdue.
 * Non-overdue snoozed conversations follow, sorted by snoozed_until (soonest first).
 *
 * @param conversations - Array of snoozed conversations
 * @returns Sorted array (does not mutate original)
 *
 * @example
 * ```typescript
 * const sorted = sortSnoozedByUrgency(conversations);
 * // [overdue tickets...], [upcoming tickets...]
 * ```
 */
export function sortSnoozedByUrgency(conversations: ChatwootConversation[]): ChatwootConversation[] {
  const now = Date.now();

  return [...conversations].sort((a, b) => {
    // Helper to check if a conversation is overdue
    const isOverdue = (conv: ChatwootConversation): boolean => {
      if (!conv.snoozed_until) return false;
      const snoozedUntilMs = typeof conv.snoozed_until === 'number'
        ? conv.snoozed_until * 1000
        : new Date(conv.snoozed_until).getTime();
      return snoozedUntilMs <= now;
    };

    // Helper to get snoozed_until timestamp in milliseconds
    const getSnoozedUntilMs = (conv: ChatwootConversation): number => {
      if (!conv.snoozed_until) return Infinity; // "until next reply" goes last
      return typeof conv.snoozed_until === 'number'
        ? conv.snoozed_until * 1000
        : new Date(conv.snoozed_until).getTime();
    };

    const aOverdue = isOverdue(a);
    const bOverdue = isOverdue(b);

    // Overdue conversations come first
    if (aOverdue && !bOverdue) return -1;
    if (!aOverdue && bOverdue) return 1;

    // Both overdue or both not overdue: sort by snoozed_until (ascending)
    // For overdue: most overdue (smallest timestamp) first
    // For not overdue: soonest to reopen (smallest timestamp) first
    const aTime = getSnoozedUntilMs(a);
    const bTime = getSnoozedUntilMs(b);
    return aTime - bTime;
  });
}

/**
 * Groups conversations by status
 *
 * @param conversations - Array of conversations
 * @returns Object with conversations grouped by status
 *
 * @example
 * ```typescript
 * const grouped = groupByStatus(conversations);
 * // { open: [...], resolved: [...], pending: [...] }
 * ```
 */
export function groupByStatus(conversations: ChatwootConversation[]): Record<string, ChatwootConversation[]> {
  return conversations.reduce((acc, conv) => {
    const status = conv.status || "unknown";
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(conv);
    return acc;
  }, {} as Record<string, ChatwootConversation[]>);
}
